/* ==========================================================================
   Detection Pages Module (100% Dynamic Multilingual Rendering across EN, CS, DE, ES, AR, PT)
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { detectionTranslations } from '../locales/detectionTranslations.js';
import { getDetectionSvg } from '../components/DetectionIcons.js';

const svgSketches = {
  "gunshot": `
      <svg viewBox="0 0 500 160" width="100%" style="display: block; background: #0a1727; border-radius: 12px; padding: 1rem;">
        <text x="20" y="22" fill="#35a8df" font-size="11" font-weight="800" letter-spacing="1">ACOUSTIC SHOCKWAVE IMPULSE ANALYSIS</text>
        <line x1="20" y1="105" x2="480" y2="105" stroke="rgba(255,255,255,0.15)" stroke-dasharray="4,4"/>
        <path d="M 20 105 L 115 105 Q 125 105 130 65 Q 135 145 140 42 Q 145 150 150 95 Q 155 115 165 105 L 480 105" fill="none" stroke="#e6273e" stroke-width="2.5"/>
        <circle cx="140" cy="42" r="4.5" fill="#e6273e"/>
        <text x="154" y="46" fill="#ffffff" font-size="10" font-weight="800">Peak Shockwave (<5s)</text>
        <g transform="translate(360, 48)">
          <rect x="0" y="0" width="100" height="42" rx="6" fill="rgba(53,168,223,0.15)" stroke="#35a8df"/>
          <text x="50" y="18" fill="#35a8df" font-size="9" font-weight="800" text-anchor="middle">PTZ AUTO-SLEW</text>
          <text x="50" y="32" fill="#ffffff" font-size="9" font-weight="700" text-anchor="middle">Bearing ± 3.5°</text>
        </g>
      </svg>
    `,
  "glass": `
      <svg viewBox="0 0 500 160" width="100%" style="display: block; background: #0a1727; border-radius: 12px; padding: 1rem;">
        <text x="20" y="22" fill="#35a8df" font-size="11" font-weight="800" letter-spacing="1">DUAL-FREQUENCY GLASS FRACTURE RESONANCE</text>
        <line x1="20" y1="110" x2="480" y2="110" stroke="rgba(255,255,255,0.15)" stroke-dasharray="4,4"/>
        <path d="M 20 110 Q 60 65 100 110 Q 140 150 180 110" fill="none" stroke="#35a8df" stroke-width="2"/>
        <text x="40" y="145" fill="#35a8df" font-size="9" font-weight="700">Initial Low Thud</text>
        <path d="M 180 110 Q 190 45 200 130 Q 210 40 220 135 Q 230 52 240 118 L 480 110" fill="none" stroke="#38bdf8" stroke-width="2"/>
        <text x="245" y="48" fill="#38bdf8" font-size="9" font-weight="800">High-Pitch Glass Fracture (3-8 kHz)</text>
      </svg>
    `,
  "drone": `
      <svg viewBox="0 0 500 160" width="100%" style="display: block; background: #0a1727; border-radius: 12px; padding: 1rem;">
        <text x="20" y="22" fill="#35a8df" font-size="11" font-weight="800" letter-spacing="1">ROTARY BLADE HARMONIC SPECTRUM</text>
        <line x1="20" y1="125" x2="480" y2="125" stroke="rgba(255,255,255,0.15)"/>
        <path d="M 20 125 L 70 125 L 80 72 L 90 125 L 150 125 L 160 48 L 170 125 L 230 125 L 240 75 L 250 125 L 480 125" fill="none" stroke="#10b981" stroke-width="2"/>
        <circle cx="160" cy="48" r="4.5" fill="#10b981"/>
        <text x="175" y="52" fill="#ffffff" font-size="10" font-weight="800">Fundamental Motor RPM Harmonic</text>
      </svg>
    `,
  "scream": `
      <svg viewBox="0 0 520 180" width="100%" style="display: block; background: #0a1727; border-radius: 12px; padding: 1.2rem;">
        <text x="20" y="22" fill="#35a8df" font-size="11" font-weight="800" letter-spacing="1">MULTI-CLASS VOCAL ACOUSTIC DISTINCTION MATRIX</text>
        
        <!-- 5 Vocal Classes Chips -->
        <g transform="translate(15, 38)">
          <rect x="0" y="0" width="92" height="42" rx="6" fill="rgba(236,72,153,0.15)" stroke="#ec4899" stroke-width="1.2"/>
          <text x="46" y="17" fill="#ec4899" font-size="9" font-weight="800" text-anchor="middle">FEMALE SCREAM</text>
          <text x="46" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">1.5 - 3.0 kHz Peak</text>
        </g>
        <g transform="translate(114, 38)">
          <rect x="0" y="0" width="92" height="42" rx="6" fill="rgba(59,130,246,0.15)" stroke="#3b82f6" stroke-width="1.2"/>
          <text x="46" y="17" fill="#3b82f6" font-size="9" font-weight="800" text-anchor="middle">MALE SCREAM</text>
          <text x="46" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">200 - 600 Hz Formants</text>
        </g>
        <g transform="translate(213, 38)">
          <rect x="0" y="0" width="92" height="42" rx="6" fill="rgba(245,158,11,0.15)" stroke="#f59e0b" stroke-width="1.2"/>
          <text x="46" y="17" fill="#f59e0b" font-size="9" font-weight="800" text-anchor="middle">CHILD SCREAM</text>
          <text x="46" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">Sharp High Harmonics</text>
        </g>
        <g transform="translate(312, 38)">
          <rect x="0" y="0" width="92" height="42" rx="6" fill="rgba(244,63,94,0.15)" stroke="#f43f5e" stroke-width="1.2"/>
          <text x="46" y="17" fill="#f43f5e" font-size="9" font-weight="800" text-anchor="middle">BABY CRY</text>
          <text x="46" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">Periodic Distress Wave</text>
        </g>
        <g transform="translate(411, 38)">
          <rect x="0" y="0" width="94" height="42" rx="6" fill="rgba(230,39,62,0.2)" stroke="#e6273e" stroke-width="1.5"/>
          <text x="47" y="17" fill="#e6273e" font-size="9" font-weight="800" text-anchor="middle">AGGRESSION</text>
          <text x="47" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">Hostile Shouting / ER</text>
        </g>

        <!-- Waveforms visualization -->
        <g transform="translate(20, 100)">
          <path d="M 0 20 Q 30 10 60 20 T 120 20" fill="none" stroke="#64748b" stroke-width="1.5" stroke-dasharray="3,3"/>
          <text x="0" y="38" fill="#64748b" font-size="7.5">Normal Talk (80-250 Hz)</text>
          <path d="M 140 20 Q 155 0 170 35 Q 185 5 200 30 Q 215 0 230 20" fill="none" stroke="#e6273e" stroke-width="2"/>
          <text x="140" y="38" fill="#e6273e" font-size="7.5" font-weight="700">Verbal Aggression Pattern</text>
          <path d="M 250 20 Q 265 -15 280 45 Q 295 -20 310 40 Q 325 -10 340 20" fill="none" stroke="#ec4899" stroke-width="2.5"/>
          <text x="250" y="38" fill="#ec4899" font-size="7.5" font-weight="700">Panic Scream Spike (>1000 Hz)</text>
          <path d="M 360 20 Q 375 5 390 20 Q 405 5 420 20 Q 435 5 450 20" fill="none" stroke="#f43f5e" stroke-width="2"/>
          <text x="360" y="38" fill="#f43f5e" font-size="7.5" font-weight="700">Rhythmic Infant Cry</text>
        </g>
        <text x="20" y="165" fill="#94a3b8" font-size="8.5" font-weight="600">SED Neural Networks independently classify Screams (acute danger) vs. Aggression (pre-assault hostility).</text>
      </svg>
    `,
  "graffiti": `
      <svg viewBox="0 0 500 160" width="100%" style="display: block; background: #0a1727; border-radius: 12px; padding: 1rem;">
        <text x="20" y="22" fill="#35a8df" font-size="11" font-weight="800" letter-spacing="1">AEROSOL HISS & AGITATOR BALL CLASSIFIER</text>
        <line x1="20" y1="110" x2="480" y2="110" stroke="rgba(255,255,255,0.15)"/>
        <line x1="60" y1="70" x2="60" y2="120" stroke="#8b5cf6" stroke-width="3"/>
        <line x1="90" y1="60" x2="90" y2="120" stroke="#8b5cf6" stroke-width="3"/>
        <text x="45" y="140" fill="#8b5cf6" font-size="9" font-weight="700">Shaker Ball Agitation</text>
        <path d="M 140 95 Q 150 85 160 105 Q 170 85 180 105 Q 190 85 200 105 T 480 95" fill="none" stroke="#a78bfa" stroke-width="2"/>
        <text x="220" y="52" fill="#a78bfa" font-size="10" font-weight="800">High-Frequency Aerosol Nozzle Hiss</text>
      </svg>
    `,
  "grinder": `
      <svg viewBox="0 0 500 160" width="100%" style="display: block; background: #0a1727; border-radius: 12px; padding: 1rem;">
        <text x="20" y="22" fill="#35a8df" font-size="11" font-weight="800" letter-spacing="1">FRICTIONAL METAL CUTTING ACOUSTIC PATTERN</text>
        <line x1="20" y1="105" x2="480" y2="105" stroke="rgba(255,255,255,0.15)"/>
        <path d="M 20 105 Q 50 40 80 150 Q 110 50 140 140 Q 170 38 200 155 Q 230 45 260 145 L 480 105" fill="none" stroke="#10b981" stroke-width="2.5"/>
        <text x="150" y="48" fill="#ffffff" font-size="10" font-weight="800">High-RPM Motor Friction & Cutting Hiss</text>
      </svg>
    `,
  "siren": `
      <svg viewBox="0 0 500 160" width="100%" style="display: block; background: #0a1727; border-radius: 12px; padding: 1rem;">
        <text x="20" y="22" fill="#35a8df" font-size="11" font-weight="800" letter-spacing="1">SIREN FREQUENCY SWEEP & TRACKING</text>
        <line x1="20" y1="90" x2="480" y2="90" stroke="rgba(255,255,255,0.15)" stroke-dasharray="4,4"/>
        <path d="M 20 125 L 100 55 L 180 125 L 260 55 L 340 125 L 420 55 L 480 105" fill="none" stroke="#06b6d4" stroke-width="2.5"/>
        <circle cx="260" cy="55" r="4.5" fill="#06b6d4"/>
        <text x="275" y="58" fill="#ffffff" font-size="10" font-weight="800">Periodic Siren Sweep (400-1200 Hz)</text>
      </svg>
    `,
  "other": `
      <svg viewBox="0 0 520 180" width="100%" style="display: block; background: #0a1727; border-radius: 12px; padding: 1.2rem;">
        <text x="20" y="22" fill="#35a8df" font-size="11" font-weight="800" letter-spacing="1">FULL SPECTRUM ACOUSTIC DETECTIONS: ALERTS &amp; EVENTS MATRIX</text>
        <g transform="translate(15, 38)">
          <rect x="0" y="0" width="92" height="42" rx="6" fill="rgba(230,39,62,0.15)" stroke="#e6273e" stroke-width="1.2"/>
          <text x="46" y="17" fill="#e6273e" font-size="9" font-weight="800" text-anchor="middle">VEHICLE &amp; DRIFT</text>
          <text x="46" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">Tire Squeal &amp; Traffic</text>
        </g>
        <g transform="translate(114, 38)">
          <rect x="0" y="0" width="92" height="42" rx="6" fill="rgba(245,158,11,0.15)" stroke="#f59e0b" stroke-width="1.2"/>
          <text x="46" y="17" fill="#f59e0b" font-size="9" font-weight="800" text-anchor="middle">FIREWORKS</text>
          <text x="46" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">Pyrotechnics Match</text>
        </g>
        <g transform="translate(213, 38)">
          <rect x="0" y="0" width="92" height="42" rx="6" fill="rgba(236,72,153,0.15)" stroke="#ec4899" stroke-width="1.2"/>
          <text x="46" y="17" fill="#ec4899" font-size="9" font-weight="800" text-anchor="middle">PANIC SCREAMS</text>
          <text x="46" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">Distress Spikes</text>
        </g>
        <g transform="translate(312, 38)">
          <rect x="0" y="0" width="92" height="42" rx="6" fill="rgba(16,185,129,0.15)" stroke="#10b981" stroke-width="1.2"/>
          <text x="46" y="17" fill="#10b981" font-size="9" font-weight="800" text-anchor="middle">EXPLOSIONS</text>
          <text x="46" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">Industrial Blasts</text>
        </g>
        <g transform="translate(411, 38)">
          <rect x="0" y="0" width="94" height="42" rx="6" fill="rgba(139,92,246,0.15)" stroke="#8b5cf6" stroke-width="1.2"/>
          <text x="47" y="17" fill="#8b5cf6" font-size="9" font-weight="800" text-anchor="middle">ATM GAS ATTACK</text>
          <text x="47" y="31" fill="#cbd5e1" font-size="7.5" text-anchor="middle">Vault Explosives</text>
        </g>

        <!-- Waveforms visualization -->
        <g transform="translate(20, 100)">
          <path d="M 0 20 L 40 20 L 50 5 L 60 35 L 70 20 L 120 20" fill="none" stroke="#e6273e" stroke-width="2"/>
          <text x="0" y="38" fill="#e6273e" font-size="7.5" font-weight="700">Vehicle Crash / Impact</text>
          <path d="M 140 20 Q 150 0 160 30 Q 170 10 180 30 Q 190 5 200 20 L 230 20" fill="none" stroke="#f59e0b" stroke-width="2"/>
          <text x="140" y="38" fill="#f59e0b" font-size="7.5" font-weight="700">Pyrotechnic Bursts</text>
          <path d="M 250 20 Q 265 -10 280 40 Q 295 -15 310 35 Q 325 -5 340 20" fill="none" stroke="#ec4899" stroke-width="2"/>
          <text x="250" y="38" fill="#ec4899" font-size="7.5" font-weight="700">Distress Shouting</text>
          <path d="M 360 20 L 375 0 L 390 40 L 405 5 L 420 30 L 450 20" fill="none" stroke="#10b981" stroke-width="2"/>
          <text x="360" y="38" fill="#10b981" font-size="7.5" font-weight="700">Industrial Blast Shockwave</text>
        </g>
        <text x="20" y="165" fill="#94a3b8" font-size="8.5" font-weight="600">SED Modular AI architecture allows custom training on client-specific acoustic threats and sound profiles.</text>
      </svg>
    `
};

const detectionUi = {
  en: {
    demoBtn: 'Request Live Acoustic Demo →',
    waveEyebrow: 'ACOUSTIC WAVEFORM & FREQUENCY ANALYSIS',
    waveTitle: 'Acoustic Signal Processing & Waveform Analysis',
    waveDesc: 'Our on-device neural models analyze continuous audio streams without transmitting raw speech or compromising privacy.',
    stepsEyebrow: 'DETECTION PIPELINE',
    stepsTitle: 'Real-Time Detection & Dispatch Pipeline',
    deepEyebrow: 'TECHNICAL ARCHITECTURE',
    deepTitle: 'Deep Neural Network Architecture & Performance',
    faqEyebrow: 'TECHNICAL FAQ',
    faqTitle: 'Frequently Asked Questions',
    videoEyebrow: 'VIDEO DEMONSTRATIONS',
    videoTitle: 'Live Field Deployments & TV News Reports',
    specsTitle: 'Technical Specifications',
    latencyLabel: 'Detection Latency:',
    latencyVal: '< 5s',
    coverageLabel: 'Detection Coverage:',
    coverageVal: 'Up to 200m radius (360°)',
    ptzLabel: 'Camera Auto-Slew:',
    ptzVal: 'Supported (ONVIF Profile S/M)',
    privacyLabel: 'Privacy Compliance:',
    privacyVal: '100% GDPR Compliant (Zero continuous audio recording)',
    vmsLabel: 'Supported VMS Platforms:',
    vmsVal: 'Milestone, Genetec, Hanwha & more',
    contactBtn: 'Request Architecture & VMS Guide →',
    exploreEyebrow: 'PORTFOLIO',
    exploreTitle: 'Explore More Acoustic Detection Types',
    overviewMainEyebrow: 'ACOUSTIC EVENT TYPES',
    overviewMainTitle: 'Real-Time Sound Event Detection Portfolio',
    overviewMainDesc: 'Explore our specialized AI sound classifiers trained to detect critical security threats in under 400 milliseconds.',
    overviewBtnDetails: 'Explore Detection Details →'
  },
  cs: {
    demoBtn: 'Požádat o živé demo akustické detekce →',
    waveEyebrow: 'ANALÝZA AKUSTICKÝCH SIGNÁLŮ A FREKVENCÍ',
    waveTitle: 'Zpracování akustického signálu a tvar vlny',
    waveDesc: 'Naše neuronové modely na koncových zařízeních analyzují nepřetržitý zvukový signál bez přenosu lidské řeči a se 100% ochranou soukromí.',
    stepsEyebrow: 'DETEKČNÍ ŘETĚZEC',
    stepsTitle: 'Řetězec detekce a odeslání poplachu v reálném čase',
    deepEyebrow: 'TECHNICKÁ ARCHITEKTURA',
    deepTitle: 'Architektura hluboké neuronové sítě a výkonnost',
    faqEyebrow: 'ČASTO KLADENÉ DOTAZY',
    faqTitle: 'Odpovědi na technické otázky',
    videoEyebrow: 'VIDEO UKÁZKY',
    videoTitle: 'Praktické nasazení a televizní reportáže',
    specsTitle: 'Technické parametry',
    latencyLabel: 'Rychlost reakce:',
    latencyVal: '< 5s',
    coverageLabel: 'Dosah detekce:',
    coverageVal: 'Až 200 m (360° všesměrově)',
    ptzLabel: 'Natočení PTZ kamery:',
    ptzVal: 'Podporováno (ONVIF Profile S/M)',
    privacyLabel: 'Ochrana soukromí:',
    privacyVal: '100% v souladu s GDPR (žádné ukládání hovorů)',
    vmsLabel: 'Kompatibilní VMS:',
    vmsVal: 'Milestone, Genetec, Hanwha a další',
    contactBtn: 'Vyžádat integrační manuál →',
    exploreEyebrow: 'PORTFOLIO',
    exploreTitle: 'Další typy akustické detekce',
    overviewMainEyebrow: 'TYPY AKUSTICKÝCH UDÁLOSTÍ',
    overviewMainTitle: 'Kompletní portfolio akustických detekcí',
    overviewMainDesc: 'Prozkoumejte naše specializované neuronové klasifikátory trénované na okamžité rozpoznání bezpečnostních hrozeb.',
    overviewBtnDetails: 'Detail detekce →'
  },
  de: {
    demoBtn: 'Live-Demo anfordern →',
    waveEyebrow: 'AKUSTISCHE SIGNALANALYSE',
    waveTitle: 'Signalverarbeitung & Wellenformanalyse',
    waveDesc: 'Unsere On-Device-KI-Modelle analysieren kontinuierliche Audioströme datenschutzkonform.',
    stepsEyebrow: 'DETEKTIONSKETTE',
    stepsTitle: 'Echtzeit-Erkennungs- und Alarmkette',
    deepEyebrow: 'TECHNISCHE ARCHITEKTUR',
    deepTitle: 'Neuronale Netzwerkarchitektur & Leistung',
    faqEyebrow: 'HÄUFIG GESTELLTE FRAGEN',
    faqTitle: 'Antworten auf technische Fragen',
    videoEyebrow: 'VIDEO-DEMONSTRATIONEN',
    videoTitle: 'Praxiseinsätze & TV-Berichte',
    specsTitle: 'Technische Spezifikationen',
    latencyLabel: 'Reaktionszeit:',
    latencyVal: '< 5s',
    coverageLabel: 'Reichweite:',
    coverageVal: 'Bis zu 200m Radius (360°)',
    ptzLabel: 'PTZ-Kamerasteuerung:',
    ptzVal: 'Unterstützt (ONVIF Profile S/M)',
    privacyLabel: 'Datenschutz:',
    privacyVal: '100% DSGVO-konform',
    vmsLabel: 'Unterstützte VMS:',
    vmsVal: 'Milestone, Genetec, Hanwha uvm.',
    contactBtn: 'Integrationshandbuch anfordern →',
    exploreEyebrow: 'PORTFOLIO',
    exploreTitle: 'Weitere Detektionsarten erkunden',
    overviewMainEyebrow: 'AKUSTISCHE EREIGNISSE',
    overviewMainTitle: 'Akustisches Echtzeit-Erkennungsportfolio',
    overviewMainDesc: 'Entdecken Sie spezialisierte neuronale Klassifikatoren zur Erkennung kritischer Sicherheitsbedrohungen.',
    overviewBtnDetails: 'Details anzeigen →'
  },
  es: {
    demoBtn: 'Solicitar demostración acústica en vivo →',
    waveEyebrow: 'ANÁLISIS DE SEÑAL ACÚSTICA',
    waveTitle: 'Procesamiento de señal acústica y forma de onda',
    waveDesc: 'Nuestros modelos neuronales analizan el flujo de audio con 100% de cumplimiento de privacidad.',
    stepsEyebrow: 'CADENA DE DETECCIÓN',
    stepsTitle: 'Cadena de detección y alerta en tiempo real',
    deepEyebrow: 'ARQUITECTURA TÉCNICA',
    deepTitle: 'Arquitectura de red neuronal profunda y rendimiento',
    faqEyebrow: 'PREGUNTAS FRECUENTES',
    faqTitle: 'Preguntas técnicas frecuentes',
    videoEyebrow: 'DEMOSTRACIONES EN VIDEO',
    videoTitle: 'Despliegues en campo e informes de televisión',
    specsTitle: 'Especificaciones técnicas',
    latencyLabel: 'Latencia de detección:',
    latencyVal: '< 5s',
    coverageLabel: 'Cobertura de detección:',
    coverageVal: 'Hasta 200m de radio (360°)',
    ptzLabel: 'Giro automático PTZ:',
    ptzVal: 'Compatible (ONVIF Profile S/M)',
    privacyLabel: 'Privacidad:',
    privacyVal: '100% compatible con RGPD',
    vmsLabel: 'Plataformas VMS compatibles:',
    vmsVal: 'Milestone, Genetec, Hanwha y más',
    contactBtn: 'Solicitar guía de integración →',
    exploreEyebrow: 'PORTAFOLIO',
    exploreTitle: 'Explorar más tipos de detección',
    overviewMainEyebrow: 'TIPOS DE EVENTOS',
    overviewMainTitle: 'Portafolio de detección de sonido en tiempo real',
    overviewMainDesc: 'Explore nuestros clasificadores acústicos entrenados para detectar amenazas críticas de seguridad.',
    overviewBtnDetails: 'Ver detalles de detección →'
  },
  pt: {
    demoBtn: 'Solicitar demonstração acústica ao vivo →',
    waveEyebrow: 'ANÁLISE DE SINAL ACÚSTICO',
    waveTitle: 'Processamento de sinal acústico e forma de onda',
    waveDesc: 'Os nossos modelos neuronais analisam o fluxo sonoro com 100% de conformidade com a privacidade.',
    stepsEyebrow: 'CADEIA DE DETEÇÃO',
    stepsTitle: 'Cadeia de deteção e envio de alarmes em tempo real',
    deepEyebrow: 'ARQUITETURA TÉCNICA',
    deepTitle: 'Arquitetura de rede neuronal profunda e desempenho',
    faqEyebrow: 'PERGUNTAS FREQUENTES',
    faqTitle: 'Perguntas técnicas frequentes',
    videoEyebrow: 'DEMONSTRAÇÕES EM VÍDEO',
    videoTitle: 'Implementações em campo e reportagens de televisão',
    specsTitle: 'Especificações técnicas',
    latencyLabel: 'Latência de deteção:',
    latencyVal: '< 5s',
    coverageLabel: 'Cobertura de deteção:',
    coverageVal: 'Raio de até 200m (360°)',
    ptzLabel: 'Giro automático PTZ:',
    ptzVal: 'Suportado (ONVIF Profile S/M)',
    privacyLabel: 'Privacidade:',
    privacyVal: '100% em conformidade com o RGPD',
    vmsLabel: 'Plataformas VMS suportadas:',
    vmsVal: 'Milestone, Genetec, Hanwha e mais',
    contactBtn: 'Solicitar guia de integração →',
    exploreEyebrow: 'PORTFÓLIO',
    exploreTitle: 'Explorar outros tipos de deteção',
    overviewMainEyebrow: 'TIPOS DE EVENTOS',
    overviewMainTitle: 'Portfólio de deteção sonora em tempo real',
    overviewMainDesc: 'Explore os nossos classificadores acústicos treinados para detetar ameaças críticas de segurança.',
    overviewBtnDetails: 'Ver detalhes de deteção →'
  },
  ar: {
    demoBtn: 'طلب عرض توضيحي صوتي حي ←',
    waveEyebrow: 'تحليل الإشارة الصوتية وشكل الموجة',
    waveTitle: 'معالجة الإشارات الصوتية وتحليل شكل الموجة',
    waveDesc: 'تقوم نماذج الذكاء الاصطناعي بتحليل الإشارات الصوتية المستمرة بخصوصية تامة 100٪.',
    stepsEyebrow: 'سلسلة الكشف',
    stepsTitle: 'سلسلة الكشف وإرسال الإنذار في الوقت الفعلي',
    deepEyebrow: 'البنية التقنية',
    deepTitle: 'بنية الشبكة العصبية العميقة والأداء',
    faqEyebrow: 'الأسئلة الشائعة',
    faqTitle: 'الأسئلة التقنية الشائعة',
    videoEyebrow: 'عروض الفيديو',
    videoTitle: 'عمليات الانتشار الميداني والتقارير التلفزيونية',
    specsTitle: 'المواصفات الفنية',
    latencyLabel: 'زمن الاستجابة:',
    latencyVal: '< 5s',
    coverageLabel: 'نطاق التغطية:',
    coverageVal: 'نصف قطر يصل إلى 200 متر (360 درجة)',
    ptzLabel: 'توجيه كاميرا PTZ الذاتي:',
    ptzVal: 'مدعوم (ONVIF Profile S/M)',
    privacyLabel: 'الامتثال للخصوصية:',
    privacyVal: 'متوافق تماماً مع GDPR بنسبة 100٪',
    vmsLabel: 'منصات VMS المدعومة:',
    vmsVal: 'Milestone و Genetec و Hanwha والمزيد',
    contactBtn: 'طلب دليل التكامل ←',
    exploreEyebrow: 'المحفظة',
    exploreTitle: 'استكشاف المزيد من أنواع الكشف',
    overviewMainEyebrow: 'أنواع الأحداث الصوتية',
    overviewMainTitle: 'محفظة كشف الأصوات في الوقت الفعلي',
    overviewMainDesc: 'استكشف مصنفاتنا الصوتية المعتمدة على الذكاء الاصطناعي لكشف التهديدات الأمنية الحرجة في أقل من 400 مللي ثانية.',
    overviewBtnDetails: 'عرض تفاصيل الكشف ←'
  }
};

const detectionImages = {
  'gunshot': '/images/Detekce_Strelba.png',
  'glass': '/images/Detekce_Sklo.png',
  'drone': '/images/dron-uvodni-obrazek.png',
  'scream': '/images/Detekce_Agrese.png',
  'graffiti': '/images/detekce-sprejovani.png',
  'grinder': '/images/Detekce_Bruska.png',
  'siren': '/images/Detekce_Alarm.png',
  'other': '/images/ostatni_vlastni_audio_detekce.png'
};

const gunshotSectorCards = [
  {
    name: {
      cs: 'Školy a univerzitní kampusy',
      en: 'Schools & University Campuses',
      de: 'Schulen & Universitätsgelände',
      es: 'Escuelas y campus universitarios',
      pt: 'Escolas e Campi Universitários',
      ar: 'المدارس والحرم الجامعي'
    },
    img: '/images/zcu-plzen.jpg'
  },
  {
    name: {
      cs: 'Chytrá města a veřejná prostranství',
      en: 'Smart Cities & Public Spaces',
      de: 'Smart Cities & Öffentliche Räume',
      es: 'Ciudades Inteligentes y Espacios Públicos',
      pt: 'Cidades Inteligentes e Espaços Públicos',
      ar: 'المدن الذكية والأماكن العامة'
    },
    img: '/images/mestska-bezpecnost.png'
  },
  {
    name: {
      cs: 'Dopravní uzly',
      en: 'Transportation Hubs',
      de: 'Verkehrsknotenpunkte',
      es: 'Centros de Transporte',
      pt: 'Centros de Transporte',
      ar: 'مراكز النقل'
    },
    img: '/images/dopravni_uzly.png'
  },
  {
    name: {
      cs: 'Kritická infrastruktura a energetika',
      en: 'Critical Infrastructure & Energy',
      de: 'Kritische Infrastruktur & Energie',
      es: 'Infraestructura Crítica y Energía',
      pt: 'Infraestrutura Crítica e Energia',
      ar: 'البنية التحتية الحيوية والطاقة'
    },
    img: '/images/prumyslova-bezpecnost.png'
  },
  {
    name: {
      cs: 'Nákupní centra a stadiony',
      en: 'Shopping Centers & Stadiums',
      de: 'Einkaufszentren & Stadien',
      es: 'Centros Comerciales y Estadios',
      pt: 'Centros Comerciais e Estádios',
      ar: 'مراكز التسوق والملاعب'
    },
    img: '/images/ochrana-komercnich-objektu.png'
  },
  {
    name: {
      cs: 'Vojenské objekty',
      en: 'Military Facilities',
      de: 'Militärische Einrichtungen',
      es: 'Instalaciones Militares',
      pt: 'Instalações Militares',
      ar: 'المنشآت العسكرية'
    },
    img: '/images/vojenske_objekty.png'
  }
];

const detectionVideosConfig = {
  gunshot: [
    {
      id: 'hL5uo1kZkU4',
      title: {
        cs: 'Lokalizace zvukových událostí – technologie AI pro rychlejší reakci',
        en: 'Sound Event Localization | AI Technology for Faster Incident Response',
        de: 'Lokalisierung von Schallereignissen | KI-Technologie für schnellere Reaktion',
        es: 'Localización de eventos sonoros | Tecnología de IA para respuesta rápida',
        pt: 'Localização de eventos sonoros | Tecnologia de IA para resposta rápida',
        ar: 'تحديد موقع الأحداث الصوتية | تقنية الذكاء الاصطناعي للاستجابة السريعة'
      },
      desc: {
        cs: 'Akustická triangulace a automatické natočení PTZ kamery na souřadnice výstřelu v reálném čase.',
        en: 'Sub-degree acoustic triangulation and automated PTZ camera auto-slewing to gunshot origin coordinates in real-time.',
        de: 'Sub-Grad-Akustiktriangulation und automatische PTZ-Kameradrehung auf Schusskoordinaten in Echtzeit.',
        es: 'Triangulación acústica y giro automático de cámara PTZ al origen del disparo en tiempo real.',
        pt: 'Triangulação acústica e rotação automática de câmara PTZ para a origem do disparo em tempo real.',
        ar: 'تثليث صوتي دقيق وتوجيه تلقائي لكاميرا PTZ إلى إحداثيات مصدر إطلاق النار في الوقت الفعلي.'
      }
    },
    {
      id: 'lFnCTiVcsW0',
      title: {
        cs: 'Test detekce střelby: 100% přesnost na vzdálenost přes 175 m',
        en: 'Gunshot Detection Test: 100% Accuracy at 175m+ Distance',
        de: 'Schusserkennungstest: 100 % Genauigkeit bei über 175 m Distanz',
        es: 'Prueba de detección de disparos: 100% de precisión a más de 175m',
        pt: 'Teste de deteção de disparos: 100% de precisão a mais de 175m',
        ar: 'اختبار كشف إطلاق النار: دقة 100٪ على مسافة تزيد عن 175 متراً'
      },
      desc: {
        cs: 'Test detekce balistických impulsů ověřující přesnost v otevřeném městském terénu.',
        en: 'Extreme-range ballistic impulse detection test verifying accuracy across wide open urban terrain.',
        de: 'Ballistischer Impulserkennungstest zur Überprüfung der Genauigkeit im städtischen Gelände.',
        es: 'Prueba de detección de impulsos balísticos de largo alcance en terreno urbano abierto.',
        pt: 'Teste de deteção de impulsos balísticos de longo alcance em terreno urbano aberto.',
        ar: 'اختبار كشف النبضات الباليستية بعيد المدى للتحقق من الدقة في المناطق الحضرية المفتوحة.'
      }
    },
    {
      id: '5MUw1I2kCaQ',
      title: {
        cs: 'Sound Event Detector – varování před střelbou při polním testování',
        en: 'Sound Event Detector | Warning for the sound of gunfire | Field Testing',
        de: 'Sound Event Detector | Schussgeräuschwarnung im Feldtest',
        es: 'Sound Event Detector | Advertencia de sonido de disparos | Pruebas de campo',
        pt: 'Sound Event Detector | Aviso de som de disparos | Testes de campo',
        ar: 'Sound Event Detector | تحذير من صوت إطلاق النار | اختبارات ميدانية'
      },
      desc: {
        cs: 'Praktický test na střelnici demonstrující reakci akustické AI pod 400 ms.',
        en: 'Live tactical firing range testing demonstrating under 400ms neural acoustic response.',
        de: 'Live-Test auf dem Schießstand mit einer neuronalen Reaktionszeit von unter 400 ms.',
        es: 'Pruebas tácticas de campo de tiro que demuestran una respuesta neuronal inferior a 400 ms.',
        pt: 'Testes táticos em campo de tiro demonstrando resposta neuronal inferior a 400 ms.',
        ar: 'اختبار حي في ميدان الرماية التكتيكي يوضح استجابة صوتية عصبية في أقل من 400 مللي ثانية.'
      }
    }
  ],
  glass: [
    {
      id: '9lt5Hz18JS4',
      title: {
        cs: 'Akustická AI detekce tříštění skla pro veřejnou bezpečnost',
        en: 'AI Glass Break Detection | Sound Event Detector for Public Safety',
        de: 'KI-Glasbrucherkennung | Sound Event Detector für öffentliche Sicherheit',
        es: 'Detección de rotura de cristal con IA | Sound Event Detector para seguridad pública',
        pt: 'Deteção de quebra de vidro com IA | Sound Event Detector para segurança pública',
        ar: 'كشف كسر الزجاج بالذكاء الاصطناعي | Sound Event Detector للأمن العام'
      },
      desc: {
        cs: 'Ověření vysokofrekvenčního prasknutí skla při simulaci vloupání do výloh a objektů.',
        en: 'High-frequency acoustic fracture validation in retail storefront and residential window breach simulations.',
        de: 'Hochfrequente akustische Bruchvalidierung bei simulierten Einbrüchen in Schaufenster und Gebäude.',
        es: 'Validación de fractura acústica de alta frecuencia en simulaciones de intrusión en vitrinas y ventanas.',
        pt: 'Validação de fratura acústica de alta frequência em simulações de intrusão em montras e janelas.',
        ar: 'التحقق من الكسر الصوتي عالي التردد في محاكاة اقتحام واجهات المتاجر والنوافذ السكنية.'
      }
    },
    {
      id: 'te0Q7Xscbr0',
      title: {
        cs: 'Polní test: 100% spolehlivost na vzdálenost 200 m (SED)',
        en: 'Field Test: 100% Reliability at 200m Distance (SED)',
        de: 'Feldtest: 100 % Zuverlässigkeit bei 200 m Distanz (SED)',
        es: 'Prueba de campo: 100% de fiabilidad a 200m de distancia (SED)',
        pt: 'Teste de campo: 100% de fiabilidade a 200m de distância (SED)',
        ar: 'اختبار ميداني: موثوقية 100٪ على مسافة 200 متر (SED)'
      },
      desc: {
        cs: 'Testování rázové vlny a rezonance tříštění skla v nepříznivých venkovních podmínkách.',
        en: 'Testing acoustic shockwave and glass fracture resonance in adverse outdoor weather conditions.',
        de: 'Testen von Stoßwellen und Glasbruchresonanz unter widrigen Witterungsbedingungen im Freien.',
        es: 'Prueba de onda expansiva y resonancia de rotura de cristal en condiciones climáticas adversas.',
        pt: 'Teste de onda de choque e ressonância de quebra de vidro em condições climáticas adversas.',
        ar: 'اختبار موجة الصدمة ورنين كسر الزجاج في الظروف الجوية الخارجية القاسية.'
      }
    }
  ],
  drone: [
    {
      id: 'LmROscO2oRY',
      title: {
        cs: 'Multimodální detekce dronů: fúze akustiky a videa',
        en: 'Multimodal Drone Detection: Acoustic + Video Fusion with SED & Osyfly.ai',
        de: 'Multimodale Drohnenerkennung: Akustik- und Videofusion mit SED & Osyfly.ai',
        es: 'Detección multimodal de drones: fusión acústica y de vídeo con SED y Osyfly.ai',
        pt: 'Deteção multimodal de drones: fusão acústica e de vídeo com SED e Osyfly.ai',
        ar: 'كشف الطائرات المسيرة متعدد الوسائط: دمج الصوت والفيديو مع SED و Osyfly.ai'
      },
      desc: {
        cs: 'Sledování neoprávněných dronů kombinací akustických frekvencí rotorů a optické AI detekce.',
        en: 'Autonomous UAV tracking fusing acoustic rotor harmonics with optical AI detection to track unauthorized drones.',
        de: 'Autonome Drohnenverfolgung durch Fusion akustischer Rotorharmonien mit optischer KI-Erkennung.',
        es: 'Seguimiento autónomo de drones fusionando armónicos acústicos de rotor con detección óptica de IA.',
        pt: 'Rastreio autónomo de drones fundindo harmónicos acústicos de rotor com deteção ótica de IA.',
        ar: 'تتبع مستقل للطائرات المسيرة من خلال دمج التوافقيات الصوتية للمراوح مع الكشف البصري بالذكاء الاصطناعي.'
      }
    },
    {
      id: 'jAYVoPYPbfw',
      title: {
        cs: 'Detekce dronů v praxi – televizní reportáž',
        en: 'Drone Detection in Practice – TV News Report',
        de: 'Drohnenerkennung in der Praxis – TV-Nachrichtenbericht',
        es: 'Detección de drones en la práctica – Reportaje de televisión',
        pt: 'Deteção de drones na prática – Reportagem de televisão',
        ar: 'كشف الطائرات المسيرة في الميدان – تقرير إخباري تلفزيوني'
      },
      desc: {
        cs: 'Reportáž ukazuje možnosti využití akustické AI pro detekci dronů a ochranu veřejného prostoru.',
        en: 'TV report demonstrating the capabilities of acoustic AI for drone detection and public space protection.',
        de: 'Fernsehbericht über die Einsatzmöglichkeiten akustischer KI zur Drohnenerkennung und zum Schutz öffentlicher Räume.',
        es: 'Reportaje que muestra el uso de la IA acústica para la detección de drones y la protección del espacio público.',
        pt: 'Reportagem que demonstra o uso da IA acústica para deteção de drones e proteção de espaços públicos.',
        ar: 'تقرير تلفزيوني يوضح إمكانيات الذكاء الاصطناعي الصوتي لكشف الطائرات المسيرة وحماية الأماكن العامة.'
      }
    }
  ],
  scream: [
    {
      id: '2sh-qBwQOG8',
      title: {
        cs: 'Sound Event Detector – ochrana studentů v kampusu (televizní reportáž)',
        en: 'Sound Event Detector | Protecting Student Safety on Campus (TV News)',
        de: 'Sound Event Detector | Schutz der Schülersicherheit auf dem Campus (TV-Bericht)',
        es: 'Sound Event Detector | Protección de la seguridad estudiantil en el campus (Reportaje TV)',
        pt: 'Sound Event Detector | Proteção da segurança de estudantes no campus (Reportagem TV)',
        ar: 'Sound Event Detector | حماية سلامة الطلاب في الحرم الجامعي (تقرير تلفزيوني)'
      },
      desc: {
        cs: 'Reportáž o klasifikaci panického křiku a nouzovém varování na chodbách škol.',
        en: 'Television report on panic scream classification and emergency notification in educational corridors.',
        de: 'Fernsehbericht über die Klassifizierung von Panikschreien und Notfallbenachrichtigungen in Schulkorridoren.',
        es: 'Reportaje de televisión sobre la clasificación de gritos de pánico y notificación de emergencia en pasillos educativos.',
        pt: 'Reportagem sobre classificação de gritos de pânico e notificação de emergência em corredores escolares.',
        ar: 'تقرير تلفزيوني حول تصنيف صرخات الذعر والإخطار في حالات الطوارئ في ممرات المدارس.'
      }
    },
    {
      id: 'gW4Nn63EyEI',
      title: {
        cs: 'Simulace útoku a reakce systému',
        en: 'Simulated Attack & System Response Drill',
        de: 'Angriffssimulation & Systemreaktion',
        es: 'Simulación de ataque y respuesta del sistema',
        pt: 'Simulação de ataque e resposta do sistema',
        ar: 'محاكاة الهجوم واستجابة النظام'
      },
      desc: {
        cs: 'Bezpečnostní cvičení demonstrující okamžitou detekci hlasového stresu a natočení kamer pod 400 ms.',
        en: 'Live active assailant drill demonstrating instant vocal stress detection and camera slew in under 400ms.',
        de: 'Sicherheitsübung zur Demonstration sofortiger Stimmerkennung und Kameradrehung in unter 400 ms.',
        es: 'Simulacro de seguridad que demuestra la detección instantánea de estrés vocal y giro de cámaras en menos de 400 ms.',
        pt: 'Simulacro de segurança demonstrando deteção instantânea de stress vocal e rotação de câmaras em menos de 400 ms.',
        ar: 'تدريب أمني يوضح الكشف الفوري عن التوتر الصوتي وتوجيه الكاميرات في أقل من 400 مللي ثانية.'
      }
    }
  ],
  graffiti: [
    {
      id: '36mSobg1P9w',
      title: {
        cs: 'Detekce vandalismu sprejováním – test systému Sound Event Detector',
        en: 'Graffiti Vandalism Detection – Sound Event Detector Test',
        de: 'Erkennung von Graffiti-Vandalismus – Test des Sound Event Detectors',
        es: 'Detección de vandalismo por grafiti – Prueba de Sound Event Detector',
        pt: 'Deteção de vandalismo por graffiti – Teste do Sound Event Detector',
        ar: 'كشف التخريب بالجرافيتي – اختبار نظام Sound Event Detector'
      },
      desc: {
        cs: 'Ukázka testování akustické detekce zvuku spreje pro včasné upozornění na možné sprejování graffiti.',
        en: 'Field demonstration of aerosol spray acoustic detection for early warning against graffiti vandalism.',
        de: 'Praxistest der akustischen Sprühgeräuscherkennung zur frühzeitigen Warnung vor Graffiti-Vandalismus.',
        es: 'Demostración de prueba de detección acústica de aerosoles para alerta temprana de vandalismo por grafiti.',
        pt: 'Demonstração de teste de deteção acústica de sprays para alerta precoce contra vandalismo.',
        ar: 'عرض توضيحي لاختبار الكشف الصوتي عن رذاذ الطلاء للتحذير المبكر من أعمال التخريب بالجرافيتي.'
      }
    }
  ],
  grinder: [
    {
      id: 'wGO_nOC-2xU',
      title: {
        cs: 'SED – simulace narušení perimetru a řezání kovu',
        en: 'SED | Sabotage Demonstration | Perimeter Cut Alert',
        de: 'SED | Sabotage-Demonstration | Perimeter-Zuschnitt-Alarm',
        es: 'SED | Demostración de sabotaje | Alerta de corte perimetral',
        pt: 'SED | Demonstração de sabotagem | Alerta de corte perimetral',
        ar: 'SED | محاكاة التخريب واختراق المحيط بقطع المعادن'
      },
      desc: {
        cs: 'Ukázka okamžité detekce řezání úhlovou bruskou při pokusu o překonání bezpečnostního oplocení.',
        en: 'Field breach demonstration showing instant detection of angle grinder cutting through high-security perimeter fences.',
        de: 'Feldversuch mit sofortiger Erkennung von Trennschleifern beim Durchtrennen von Sicherheitszäunen.',
        es: 'Demostración que muestra la detección instantánea de amoladoras angulares cortando vallas de seguridad.',
        pt: 'Demonstração em campo que mostra a deteção instantânea de rebarbadoras a cortar vedações de segurança.',
        ar: 'عرض ميداني يوضح الكشف الفوري عن استخدام قاطع المعادن (الصاروخ) لقطع الأسوار الأمنية.'
      }
    }
  ],
  siren: [
    {
      id: 'WTtDUz9FT7Q',
      title: {
        cs: 'Sound Event Detector – rozpoznávání sirén vozidel IZS',
        en: 'Sound Event Detector | Emergency Siren & Vehicle Recognition',
        de: 'Sound Event Detector | Erkennung von Notfallsirenen und Fahrzeugen',
        es: 'Sound Event Detector | Reconocimiento de sirenas de emergencia y vehículos',
        pt: 'Sound Event Detector | Reconhecimento de sirenes de emergência e veículos',
        ar: 'Sound Event Detector | التعرف على صفارات الإنذار ومركبات الطوارئ'
      },
      desc: {
        cs: 'Sledování sirén záchranných složek (policie, záchranná služba, hasiči) pro městské koridory a křižovatky.',
        en: 'Tracking emergency vehicle sirens (police, ambulance, fire) across municipal traffic corridors and intersections.',
        de: 'Erfassung von Einsatzfahrzeugsirenen (Polizei, Rettungswagen, Feuerwehr) für städtische Verkehrskorridore.',
        es: 'Seguimiento de sirenas de emergencia (policía, ambulancia, bomberos) en intersecciones urbanas.',
        pt: 'Rastreio de sirenes de veículos de emergência (polícia, ambulância, bombeiros) em cruzamentos urbanos.',
        ar: 'تتبع صفارات إنذار مركبات الطوارئ (الشرطة، الإسعاف، الإطفاء) عبر الممرات والتقاطعات المرورية.'
      }
    }
  ],
  other: [
    {
      id: 'quk8DXZKkeE',
      title: {
        cs: 'Návrh pokrytí a maximalizace výkonu detekce',
        en: 'Coverage Design | Maximizing Detection Performance',
        de: 'Abdeckungsdesign | Maximierung der Erkennungsleistung',
        es: 'Diseño de cobertura | Maximización del rendimiento de detección',
        pt: 'Design de cobertura | Maximização do desempenho de deteção',
        ar: 'تصميم التغطية | تعظيم أداء الكشف الصوتي'
      },
      desc: {
        cs: 'Odborné akustické modelování a návrh pokrytí pro členité architektonické prostory.',
        en: 'Expert acoustic modeling and coverage design for complex architectural layouts.',
        de: 'Fachgerechte akustische Modellierung und Abdeckungsplanung für komplexe Gebäudestrukturen.',
        es: 'Modelado acústico experto y diseño de cobertura para espacios arquitectónicos complejos.',
        pt: 'Modelação acústica especializada e planeamento de cobertura para espaços arquitetónicos complexos.',
        ar: 'نمذجة صوتية متخصصة وتصميم تغطية للمساحات المعمارية المعقدة.'
      }
    },
    {
      id: 'V5ZEUhvtGzU',
      title: {
        cs: 'Praktická ukázka pokročilé akustické detekce',
        en: 'Advanced Acoustic Detection in Practice',
        de: 'Fortgeschrittene akustische Erkennung in der Praxis',
        es: 'Detección acústica avanzada en la práctica',
        pt: 'Deteção acústica avançada na prática',
        ar: 'الكشف الصوتي المتقدم في الميدان'
      },
      desc: {
        cs: 'Představení schopností akustické AI pro detekci dalších zvukových událostí v různých prostředích.',
        en: 'Showcase of acoustic AI capabilities for detecting situational and operational sound events across diverse environments.',
        de: 'Präsentation der akustischen KI-Fähigkeiten zur Erkennung situativer und betrieblicher Schallereignisse.',
        es: 'Demostración de capacidades de IA acústica para detectar eventos situacionales y operativos en diversos entornos.',
        pt: 'Apresentação das capacidades da IA acústica para detetar eventos situacionais e operacionais em múltiplos ambientes.',
        ar: 'استعراض قدرات الذكاء الاصطناعي الصوتي لكشف الأحداث الظرفية والتشغيلية في بيئات متنوعة.'
      }
    }
  ]
};

function getStepSvgIcon(idx, detKey) {
  const icons = [
    // Step 1: Acoustic Wave / Microphone / Sensor
    `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="22"></line></svg>`,
    // Step 2: Neural AI / Brain / Pattern Match
    `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-2.04Z"></path><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-2.04Z"></path></svg>`,
    // Step 3: PTZ Camera / Auto-Slew
    `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path><circle cx="12" cy="13" r="3"></circle></svg>`,
    // Step 4: Dispatch / VMS Alarm / Siren
    `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>`
  ];
  return icons[idx % icons.length];
}

export function renderDetectionPage(routeKey = 'detection', onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';

  if (typeof routeKey === 'function') {
    onNavigate = routeKey;
    routeKey = window.location.hash || 'detection';
  }

  let detKey = null;
  const strRoute = String(routeKey || '');
  if (strRoute.includes('gunshot')) detKey = 'gunshot';
  else if (strRoute.includes('glass')) detKey = 'glass';
  else if (strRoute.includes('drone')) detKey = 'drone';
  else if (strRoute.includes('scream')) detKey = 'scream';
  else if (strRoute.includes('graffiti')) detKey = 'graffiti';
  else if (strRoute.includes('grinder')) detKey = 'grinder';
  else if (strRoute.includes('siren')) detKey = 'siren';
  else if (strRoute.includes('other')) detKey = 'other';

  const lang = getLocale() || 'en';
  const ui = detectionUi[lang] || detectionUi.en;
  const langDict = detectionTranslations[lang] || detectionTranslations['en'] || {};
  const enDict = detectionTranslations['en'] || {};

  // SUBPAGE RENDERING
  if (detKey && (langDict[detKey] || enDict[detKey])) {
    const loc = langDict[detKey] || enDict[detKey] || {};
    const fallback = enDict[detKey] || {};

    const meta = {
      eyebrow: loc.eyebrow || fallback.eyebrow || 'ACOUSTIC DETECTION',
      title: loc.title || fallback.title || detKey.toUpperCase(),
      img: detectionImages[detKey] || '/images/Detekce_Strelba.png',
      lead: loc.lead || fallback.lead || '',
      gain: loc.gain || fallback.gain || '<5s Response',
      filter: loc.filter || fallback.filter || 'Acoustic Classifier',
      demoBtn: loc.demoBtn || fallback.demoBtn || ui.demoBtn,
      steps: loc.steps || fallback.steps || []
    };
    const sketchSvg = svgSketches[detKey] || svgSketches['gunshot'];
    const sections = loc.sections || fallback.sections || [];
    const faqs = loc.faqs || fallback.faqs || [];

    const videos = (detectionVideosConfig[detKey] || []).map(v => ({
      id: v.id,
      title: v.title[lang] || v.title.en,
      desc: v.desc[lang] || v.desc.en
    }));

    container.innerHTML = `
      <!-- Hero Header -->
      <section class="sed-section" style="background: linear-gradient(165deg, var(--navy-dark) 0%, var(--blue-dark) 100%); color: #ffffff; padding: 4.5rem 0 3.5rem;">
        <div class="sed-container">
          <div style="display: flex; gap: 0.5rem; align-items: center; font-size: 0.85rem; color: #cbd5e1; margin-bottom: 1.5rem; flex-wrap: wrap;">
            <a href="#home" style="color: #ffffff; text-decoration: none;" data-route="home">Home</a> /
            <a href="#detection" style="color: #ffffff; text-decoration: none;" data-route="detection">${t('nav.detection')}</a> /
            <span style="color: var(--blue-primary); font-weight: 700;">${meta.title}</span>
          </div>

          <div class="sed-detail-hero-grid">
            <div>
              <div class="sed-eyebrow" style="background: rgba(255,255,255,0.1); color: var(--blue-primary); border-color: rgba(255,255,255,0.2);">${meta.eyebrow}</div>
              <h1 class="sed-page-hero-title">
                ${meta.title}
              </h1>
              <p style="font-size: 1.15rem; color: #e2e8f0; line-height: 1.65; margin-bottom: 2rem;">
                ${meta.lead}
              </p>
              <a href="#contact" class="sed-btn sed-btn-hero sed-btn-primary" data-route="contact">${meta.demoBtn}</a>
            </div>
            <div style="border-radius: var(--radius-card); overflow: hidden; border: 2px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.05); padding: 1.5rem; text-align: center;">
              <img src="${meta.img}" alt="${meta.title}" style="width: 100%; max-height: 340px; object-fit: contain; border-radius: 8px;">
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content Layout with Technical Sidebar -->
      <section class="sed-section" style="background-color: #ffffff;">
        <div class="sed-container">
          <div class="sed-detail-main-grid">
            
            <!-- Left Column: Detailed Sections & FAQs -->
            <div>
              
              <!-- Acoustic Sketch / Diagram -->
              <div style="margin-bottom: 3.5rem;">
                <div class="sed-eyebrow">${ui.waveEyebrow}</div>
                <h2 class="sed-detail-section-title">
                  ${ui.waveTitle}
                </h2>
                <p style="color: var(--text-muted); font-size: 1.02rem; margin-bottom: 1.5rem; line-height: 1.6;">
                  ${ui.waveDesc}
                </p>
                <div style="box-shadow: var(--shadow-sm); border-radius: 12px; overflow: hidden; border: 1px solid var(--slate-line);">
                  ${sketchSvg}
                </div>
              </div>

              <!-- Step-by-Step Acoustic Process (Connected Process Flow) -->
              ${meta.steps && meta.steps.length > 0 ? `
                <div style="margin-bottom: 3.5rem;">
                  <div class="sed-eyebrow">${ui.stepsEyebrow}</div>
                  <h2 class="sed-detail-section-title">
                    ${ui.stepsTitle}
                  </h2>
                  <div style="display: flex; flex-direction: row; gap: 1rem; align-items: stretch; flex-wrap: wrap;">
                    ${meta.steps.map((step, idx) => `
                      <div class="sed-card" style="flex: 1 1 200px; min-width: 170px; padding: 1.4rem; background: #ffffff; border: 1.5px solid var(--slate-line); border-radius: 12px; position: relative; display: flex; flex-direction: column; justify-content: flex-start; box-shadow: 0 4px 12px rgba(0,0,0,0.03);">
                        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.85rem;">
                          <span style="display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: var(--blue-primary); color: #ffffff; font-weight: 800; font-size: 0.85rem;">
                            ${idx + 1}
                          </span>
                          <div style="color: var(--blue-primary); width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;">
                            ${getStepSvgIcon(idx, detKey)}
                          </div>
                        </div>
                        <h4 style="font-family: var(--font-heading); font-size: 0.98rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.4rem; line-height: 1.35;">
                          ${step.title.replace(/^(\d+\.|\w+\:)\s*/, '')}
                        </h4>
                        <p style="font-size: 0.86rem; color: var(--text-dark); line-height: 1.5; margin: 0;">
                          ${step.desc}
                        </p>
                      </div>
                    `).join('')}
                  </div>
                </div>
              ` : ''}

              <!-- Deep-Dive Multi-Language Sections -->
              <div style="margin-bottom: 3.5rem;">
                <div class="sed-eyebrow">${ui.deepEyebrow}</div>
                <h2 class="sed-detail-section-title">
                  ${ui.deepTitle}
                </h2>
                
                ${sections.map((sec, idx) => `
                  <div style="margin-bottom: 2rem; background: #ffffff; border: 1px solid var(--slate-line); border-radius: var(--radius-card); padding: 1.75rem; box-shadow: var(--shadow-sm);">
                    <h3 class="sed-sub-title" style="color: var(--blue-primary); margin-bottom: 0.85rem;">
                      ${sec.title}
                    </h3>
                    <p style="font-size: 1.02rem; color: var(--text-dark); line-height: 1.75; margin-bottom: ${(sec.bullets && sec.bullets.length) || (detKey === 'gunshot' && idx === 1) ? '1.25rem' : '0'};">
                      ${sec.desc}
                    </p>

                    <!-- Gunshot: 6 Photo Cards Grid for Target Deployment Sectors -->
                    ${detKey === 'gunshot' && idx === 1 ? `
                      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.25rem; margin-top: 1rem;">
                        ${gunshotSectorCards.map(s => `
                          <div style="position: relative; height: 160px; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.08); border: 1px solid var(--slate-line);">
                            <img src="${s.img}" alt="${s.name[lang] || s.name.en}" style="width: 100%; height: 100%; object-fit: cover;">
                            <div style="position: absolute; inset: 0; background: linear-gradient(180deg, rgba(10, 23, 39, 0.2) 0%, rgba(10, 23, 39, 0.85) 100%);"></div>
                            <div style="position: absolute; bottom: 0; left: 0; right: 0; padding: 0.85rem; color: #ffffff; font-family: var(--font-heading); font-weight: 800; font-size: 0.92rem; line-height: 1.35; text-shadow: 0 1px 3px rgba(0,0,0,0.6);">
                              ${s.name[lang] || s.name.en}
                            </div>
                          </div>
                        `).join('')}
                      </div>
                    ` : (sec.bullets && sec.bullets.length ? `
                      <ul style="padding-left: 1.25rem; display: flex; flex-direction: column; gap: 0.6rem; margin: 0;">
                        ${sec.bullets.map(b => `
                          <li style="font-size: 0.95rem; color: var(--text-dark); line-height: 1.55;">
                            ${b.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}
                          </li>
                        `).join('')}
                      </ul>
                    ` : '')}
                  </div>
                `).join('')}
              </div>

              <!-- FAQ Section -->
              ${faqs && faqs.length > 0 ? `
                <div style="margin-bottom: 3.5rem;">
                  <div class="sed-eyebrow">${ui.faqEyebrow}</div>
                  <h2 class="sed-detail-section-title">
                    ${ui.faqTitle}
                  </h2>
                  <div style="display: flex; flex-direction: column; gap: 1rem;">
                    ${faqs.map(faq => `
                      <details class="sed-card" style="padding: 1.25rem 1.5rem; cursor: pointer; background: #ffffff; border: 1px solid var(--slate-line);">
                        <summary style="font-weight: 800; color: var(--navy-dark); font-size: 1.05rem; list-style: none; display: flex; justify-content: space-between; align-items: center;">
                          <span>❓ ${faq.q}</span>
                          <span style="color: var(--blue-primary); font-size: 1.1rem; font-weight: 800;">▾</span>
                        </summary>
                        <p style="margin-top: 1rem; font-size: 0.98rem; color: var(--text-dark); line-height: 1.65; border-top: 1px solid var(--slate-line); padding-top: 0.85rem; margin-bottom: 0;">
                          ${faq.a}
                        </p>
                      </details>
                    `).join('')}
                  </div>
                </div>
              ` : ''}

              <!-- Related Demonstration Videos -->
              ${videos && videos.length > 0 ? `
                <div style="margin-bottom: 2rem;">
                  <div class="sed-eyebrow">${ui.videoEyebrow}</div>
                  <h2 class="sed-detail-section-title">
                    ${ui.videoTitle}
                  </h2>
                  <div style="display: grid; grid-template-columns: 1fr; gap: 1.5rem;">
                    ${videos.map(v => `
                      <div class="sed-card" style="padding: 1.25rem; background: #ffffff; border: 1px solid var(--slate-line);">
                        <div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 8px; margin-bottom: 1rem;">
                          <iframe src="https://www.youtube.com/embed/${v.id}" title="${v.title}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
                        </div>
                        <h4 style="font-family: var(--font-heading); font-size: 1.05rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.35rem;">${v.title}</h4>
                        <p style="font-size: 0.9rem; color: var(--text-muted); line-height: 1.55; margin: 0;">${v.desc}</p>
                      </div>
                    `).join('')}
                  </div>
                </div>
              ` : ''}

            </div>

            <!-- Right Column: Specs & Other Classifiers Navigation -->
            <div class="sed-detail-sidebar">
              
              <!-- Card 1: Technical Parameters -->
              <div class="sed-card" style="background: #ffffff; border: 1px solid var(--slate-line); padding: 1.75rem; box-shadow: var(--shadow-sm);">
                <div style="font-size: 0.78rem; font-weight: 800; color: var(--blue-primary); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.3rem;">EDGE AI HARDWARE & SW</div>
                <h3 class="sed-sidebar-title" style="margin-bottom: 1.25rem;">
                  ${ui.specsTitle}
                </h3>

                <div style="display: flex; flex-direction: column; gap: 0.85rem; font-size: 0.92rem;">
                  <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; border-bottom: 1px dashed var(--slate-line); padding-bottom: 0.5rem;">
                    <strong style="color: var(--navy-dark); flex-shrink: 0; white-space: nowrap;">${ui.latencyLabel}</strong>
                    <span style="color: var(--blue-primary); font-weight: 800; text-align: right; margin-left: auto;">${ui.latencyVal}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; border-bottom: 1px dashed var(--slate-line); padding-bottom: 0.5rem;">
                    <strong style="color: var(--navy-dark); flex-shrink: 0; white-space: nowrap;">${ui.coverageLabel}</strong>
                    <span style="color: var(--navy-dark); font-weight: 600; text-align: right; margin-left: auto;">${ui.coverageVal}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; border-bottom: 1px dashed var(--slate-line); padding-bottom: 0.5rem;">
                    <strong style="color: var(--navy-dark); flex-shrink: 0; white-space: nowrap;">${ui.ptzLabel}</strong>
                    <span style="color: var(--navy-dark); font-weight: 600; text-align: right; margin-left: auto;">${ui.ptzVal}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; border-bottom: 1px dashed var(--slate-line); padding-bottom: 0.5rem;">
                    <strong style="color: var(--navy-dark); flex-shrink: 0; white-space: nowrap;">${ui.privacyLabel}</strong>
                    <span style="color: var(--navy-dark); font-weight: 600; text-align: right; margin-left: auto; line-height: 1.4;">${ui.privacyVal}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; border-bottom: 1px dashed var(--slate-line); padding-bottom: 0.5rem;">
                    <strong style="color: var(--navy-dark); flex-shrink: 0; white-space: nowrap;">${ui.vmsLabel}</strong>
                    <span style="text-align: right; margin-left: auto;">
                      <a href="#solutions" style="color: var(--blue-primary); font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; font-size: 0.88rem;" data-route="solutions">
                        ${ui.vmsVal} →
                      </a>
                    </span>
                  </div>
                </div>

                <a href="#contact" class="sed-btn sed-btn-primary" style="width: 100%; text-align: center; margin-top: 1.75rem; font-size: 0.88rem;" data-route="contact">${ui.contactBtn}</a>
              </div>

              <!-- Card 2: Other Classifiers Navigation -->
              <div class="sed-card" style="background: var(--blue-ultra-light); border: 1px solid var(--blue-light); padding: 1.75rem;">
                <div style="font-size: 0.78rem; font-weight: 800; color: var(--blue-primary); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.3rem;">
                  ${ui.exploreEyebrow}
                </div>
                <h3 class="sed-sidebar-title">
                  ${ui.exploreTitle}
                </h3>

                <div style="display: flex; flex-direction: column; gap: 0.6rem;">
                  <a href="#detection-gunshot" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid #e6273e; background: #ffffff;" data-route="detection-gunshot">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: #e6273e; display: inline-flex;">${getDetectionSvg('gunshot', 16, '#e6273e')}</span>
                      ${t('detectionTypes.gunshot')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#detection-glass" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid var(--blue-primary); background: #ffffff;" data-route="detection-glass">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: var(--blue-primary); display: inline-flex;">${getDetectionSvg('glass', 16, 'var(--blue-primary)')}</span>
                      ${t('detectionTypes.glass')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#detection-drone" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid var(--navy-dark); background: #ffffff;" data-route="detection-drone">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: var(--navy-dark); display: inline-flex;">${getDetectionSvg('drone', 16, 'var(--navy-dark)')}</span>
                      ${t('detectionTypes.drone')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#detection-scream" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid #f59e0b; background: #ffffff;" data-route="detection-scream">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: #f59e0b; display: inline-flex;">${getDetectionSvg('scream', 16, '#f59e0b')}</span>
                      ${t('detectionTypes.scream')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#detection-graffiti" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid #8b5cf6; background: #ffffff;" data-route="detection-graffiti">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: #8b5cf6; display: inline-flex;">${getDetectionSvg('graffiti', 16, '#8b5cf6')}</span>
                      ${t('detectionTypes.graffiti')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#detection-grinder" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid #10b981; background: #ffffff;" data-route="detection-grinder">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: #10b981; display: inline-flex;">${getDetectionSvg('grinder', 16, '#10b981')}</span>
                      ${t('detectionTypes.grinder')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#detection-siren" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid #06b6d4; background: #ffffff;" data-route="detection-siren">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: #06b6d4; display: inline-flex;">${getDetectionSvg('siren', 16, '#06b6d4')}</span>
                      ${t('detectionTypes.siren')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#detection-other" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; align-items: center; justify-content: space-between; border-left: 3px solid #64748b; background: #ffffff;" data-route="detection-other">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: #64748b; display: inline-flex;">${getDetectionSvg('other', 16, '#64748b')}</span>
                      ${t('detectionTypes.other')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>
    `;

    container.querySelectorAll('[data-route]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (onNavigate) onNavigate(link.getAttribute('data-route'));
      });
    });

    return container;
  }

  // OVERVIEW CATALOG PAGE (100% LOCALIZED)
  const overviewTexts = {
    eyebrow: ui.overviewMainEyebrow,
    title: ui.overviewMainTitle,
    desc: ui.overviewMainDesc,
    btnDetails: ui.overviewBtnDetails
  };

  const detectionCards = [
    { key: 'gunshot', color: '#e6273e', bg: 'rgba(230, 39, 62, 0.08)' },
    { key: 'glass', color: 'var(--blue-primary)', bg: 'rgba(53, 168, 223, 0.08)' },
    { key: 'drone', color: 'var(--navy-dark)', bg: 'rgba(10, 23, 39, 0.06)' },
    { key: 'scream', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)' },
    { key: 'graffiti', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.08)' },
    { key: 'grinder', color: '#10b981', bg: 'rgba(16, 185, 129, 0.08)' },
    { key: 'siren', color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.08)' },
    { key: 'other', color: '#64748b', bg: 'rgba(100, 116, 139, 0.08)' }
  ];

  container.innerHTML = `
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem;">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 840px;">
          <div class="sed-eyebrow">${overviewTexts.eyebrow}</div>
          <h1 class="sed-page-hero-title">${overviewTexts.title}</h1>
          <p class="sed-section-desc">${overviewTexts.desc}</p>
        </div>
      </div>
    </section>

    <!-- Detection Grid -->
    <section class="sed-section" style="background-color: #ffffff;">
      <div class="sed-container">
        <div class="sed-grid-3">
          ${detectionCards.map(item => {
            const locItem = (langDict[item.key] || enDict[item.key] || {});
            const title = locItem.title || t(`detectionTypes.${item.key}`);
            const lead = locItem.lead || locItem.intro || '';
            const route = `detection-${item.key}`;

            return `
              <div class="sed-card" style="padding: 2rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 4px solid ${item.color};">
                <div>
                  <div style="width: 56px; height: 56px; border-radius: 12px; background: ${item.bg}; display: flex; align-items: center; justify-content: center; margin-bottom: 1.25rem; color: ${item.color}; border: 1px solid rgba(0,0,0,0.04);">
                    ${getDetectionSvg(item.key, 28, item.color)}
                  </div>
                  <h3 class="sed-card-title">${title}</h3>
                  <p class="sed-card-text" style="font-size: 0.92rem; line-height: 1.6; margin-bottom: 1.25rem;">
                    ${lead}
                  </p>
                </div>
                <a href="#${route}" class="sed-btn sed-btn-primary" style="text-align: center; font-size: 0.85rem;" data-route="${route}">
                  ${overviewTexts.btnDetails}
                </a>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    </section>
  `;

  container.querySelectorAll('[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (onNavigate) onNavigate(link.getAttribute('data-route'));
    });
  });

  return container;
}
